import React from "react";
import { Button } from "primereact/button";
import styles from "./OverviewPanes.module.css";

export type BadgeTone = "override" | "layer" | "custom" | "info";

export const RowBadge: React.FC<{
  tone: BadgeTone;
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span className={`${styles.badge} ${styles[`badge_${tone}`]}`}>
    {children}
  </span>
);

/** Label column plus a control column; the building block of both panes. */
export const FieldRow: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  muted?: boolean;
  children: React.ReactNode;
}> = ({ label, hint, actions, muted, children }) => (
  <div className={`${styles.row} ${muted ? styles.rowMuted : ""}`}>
    <div className={styles.label}>
      <span>{label}</span>
      {hint && <span className={styles.labelHint}>{hint}</span>}
    </div>
    <div className={styles.control}>{children}</div>
    {actions && <div className={styles.actions}>{actions}</div>}
  </div>
);

/** A value the user can see but not change here; ``note`` says why. */
export const ReadOnlyRow: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  note?: string;
}> = ({ label, value, hint, note }) => (
  <FieldRow label={label} hint={hint}>
    <span className={styles.readonly}>
      {value === null || value === undefined || value === "" ? (
        <span className={styles.blank}>—</span>
      ) : (
        value
      )}
    </span>
    {note && <span className={styles.readonlyNote}>{note}</span>}
  </FieldRow>
);

/**
 * Click-to-edit row: shows ``display`` until toggled, then ``editor``.
 * ``extra`` is a second action slot (revert, unlink) shown in both modes.
 */
export const EditableRow: React.FC<{
  label: React.ReactNode;
  display: React.ReactNode;
  editing: boolean;
  onToggle: () => void;
  editor: React.ReactNode;
  hint?: React.ReactNode;
  badge?: React.ReactNode;
  extra?: React.ReactNode;
}> = ({ label, display, editing, onToggle, editor, hint, badge, extra }) => {
  const blank = display === null || display === undefined || display === "";
  return (
    <FieldRow
      label={label}
      hint={hint}
      actions={
        <>
          <Button
            icon={editing ? "pi pi-check" : "pi pi-pencil"}
            rounded
            text
            size="small"
            tooltip={editing ? "Done" : "Edit"}
            tooltipOptions={{ showDelay: 400 }}
            onClick={onToggle}
            type="button"
          />
          {extra}
        </>
      }
    >
      {editing ? (
        editor
      ) : (
        <span
          className={`${styles.viewValue} ${blank ? styles.blank : ""}`}
          onClick={onToggle}
        >
          {blank ? "—" : display}
        </span>
      )}
      {badge}
    </FieldRow>
  );
};

/** Section card with the uppercase title strip both panes use. */
export const Section: React.FC<{
  title: React.ReactNode;
  hint?: React.ReactNode;
  count?: number;
  children: React.ReactNode;
}> = ({ title, hint, count, children }) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>
      <span>{title}</span>
      {hint && <span className={styles.sectionHint}>{hint}</span>}
      {count !== undefined && (
        <span className={styles.sectionCount}>{count}</span>
      )}
    </h2>
    <div className={styles.rows}>{children}</div>
  </section>
);
